import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Input from '@Components/Input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { Checkbox } from '@els/els-react--checkbox';
import Button from '@Components/Button';
import LoginFormHeader from './LoginFormHeader';
import LoginBlockedMessage from './LoginBlockedMessage';
import './LoginForm.custom.scss';
import './LoginForm.theme.scss';

let timerConst;

function LoginForm({
  isAnonymousUser,
  cancel,
  ipAddress,
  loginFail,
  loginFailMessage,
  isLoading,
  isFormDisabled,
  openForgotPasswordDialog,
  submit,
  hasInstitutionLogin,
  hasRegistration,
  loginBlockedTime,
  data,
  setData,
}) {
  const spinnerId = 'loginForm';
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [timer, setTimer] = useState(null);

  const clearTimer = () => {
    clearTimeout(timerConst);
  };

  useEffect(() => {
    if (timerConst) {
      clearTimer();
    }

    if (!loginBlockedTime && loginBlockedTime !== 0) {
      return;
    }

    const roundedDifference = Math.round(loginBlockedTime);
    if (roundedDifference <= 300 && roundedDifference >= 0) {
      const seconds = 300 - roundedDifference;
      setTimer(seconds);

      setTimeout(() => setTimer(null), seconds * 1000);
    }

    // eslint-disable-next-line consistent-return
    return () => clearTimer();
  }, [loginBlockedTime]);

  const resetPassword = (e) => {
    e.preventDefault();
    openForgotPasswordDialog();
  };

  const submitHandler = () => {
    if (isFormDisabled) {
      return;
    }
    setIsSubmitted(true);
    submit(data);
  };

  /*
   * loginFailMessage - the error message which is returned on login page itself, e.g. incorrect credentials
   * loginFail - the error message which is passed from other components, e.g. from AuthInterceptor
   */
  const showErrorMessage = loginFail || loginFailMessage;
  const changeUserName = (value) => {
    setData({
      username: value,
    });
  };

  const changePassword = (value) => {
    setData({
      password: value,
    });
  };

  const changeRememberMe = (e) => {
    setData({
      remember_me: e.target.checked,
    });
  };

  return (
    <div className="login-form login-page-form-wrapper">
      <LoginFormHeader
        cancel={cancel}
        isAnonymousUser={isAnonymousUser}
        ipAddress={ipAddress}
        actionMessage="Sign in"
        infoMessage="With your Reaxys Account">
        <LoginBlockedMessage timer={timer} />
      </LoginFormHeader>
      <form name="loginForm">
        <div className="login-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="els-text-input">
            <Input
              clearButton={false}
              id="id-login-username"
              name="username"
              classNameInput="e2e-login-username"
              animatedPlaceholder
              disabled={isFormDisabled}
              placeholder="Username"
              value={data.username}
              onChange={changeUserName}
              debounceDelay={0}
              validationMessage={showErrorMessage && ''}
              required
            />
          </div>
          <div className="els-text-input">
            <Input
              id="id-login-password"
              name="password"
              clearButton={false}
              disabled={isFormDisabled}
              classNameInput="e2e-login-password"
              animatedPlaceholder
              placeholder="Password"
              value={data.password}
              onChange={changePassword}
              debounceDelay={0}
              validationMessage={showErrorMessage && ''}
              required
              type="password"
            />
          </div>
          {showErrorMessage && (
            <div className="login-form__content_error u-els-margin-bottom">{isSubmitted ? loginFailMessage : loginFail}</div>
          )}

          <div className="els-text-input els-checkbox-input">
            <div className="login-form__content_remember">
              <Checkbox
                onChange={changeRememberMe}
                checked={data.remember_me}
                disabled={isFormDisabled}
                id="login_remember"
                ariaControlsGuidance="Remember me on this computer .Do not use on a shared computer!"
              />
              <div className="login-form__content_remember-text">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="login_remember" title="Remember me on this computer">
                  <span>Remember me on this computer</span>
                </label>
                <span>(Do not use on a shared computer!)</span>
              </div>
            </div>
          </div>
          {hasRegistration && (
            <span
              className="login-form__message typography-meta signup-trouble"
              data-click-delegation
              data-tracking="true"
              data-tracking-type="productfeatureused"
              data-tracking-action="not registered">
              <a className="blue-link" href="#/security/register" tabIndex="0">
                Not Registered?
              </a>
            </span>
          )}
          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button">
              <Button
                buttonConfig={{
                  type: 'primary',
                  size: 'md',
                  label: 'Sign in',
                  e2eName: 'login-sign-in',
                }}
                iconConfig={{
                  name: 'chevron-right',
                  size: 'sm',
                }}
                disabled={isLoading || timer > 0}
                onClick={submitHandler}
                type="button"
              />
            </div>
          </div>
        </div>
      </form>
      {hasInstitutionLogin && (
        <span className="login-form__message typography-meta">
          <a className="blue-link" href="#/institution" tabIndex="0">
            Sign in via your institution
          </a>
        </span>
      )}
      <span className="login-form__message typography-meta">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className="blue-link" tabIndex="0" href="#" onClick={resetPassword}>
          Forgot your password?
        </a>
      </span>
      <span className="login-form__message typography-meta">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a tabIndex="0" className="blue-link hidden-beta" href="#">
          Trouble signing in?
        </a>
      </span>
    </div>
  );
}

LoginForm.propTypes = {
  isAnonymousUser: PropTypes.bool,
  cancel: PropTypes.func.isRequired,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.string,
  loginFailMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  isFormDisabled: PropTypes.bool,
  openForgotPasswordDialog: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  hasInstitutionLogin: PropTypes.bool,
  hasRegistration: PropTypes.bool,
  loginBlockedTime: PropTypes.number,
  data: PropTypes.object,
  setData: PropTypes.func,
};

export default LoginForm;
